import type { ITenderBid } from "../../types";

const STOP = new Set([
  "the",
  "and",
  "of",
  "for",
  "to",
  "a",
  "an",
  "in",
  "on",
  "tender",
  "bid",
  "response",
  "framework",
  "services",
  "service",
  "contract",
  "council",
  "ltd",
  "limited",
  "plc",
  "draft",
  "final",
  "v1",
  "v2",
  "copy",
]);

const tokens = (text: string): Set<string> =>
  new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .split(" ")
      .filter((t) => t.length > 2 && !STOP.has(t))
  );

/**
 * The tender a document most likely belongs to, from its title and first
 * heading against the bid titles and client names. Two shared meaningful
 * words (or one word of 6+ letters, e.g. a place or a reference number) is
 * enough to suggest; ties go to the most recently updated bid, which the
 * list is already sorted by.
 * @param documentTitle - The document's title, or its first heading.
 * @param tenders - The bids, most recent first.
 * @returns The suggested bid, or null.
 */
export const suggestTender = (
  documentTitle: string | null | undefined,
  tenders: readonly ITenderBid[]
): ITenderBid | null => {
  if (!documentTitle?.trim() || !tenders.length) return null;
  const doc = tokens(documentTitle);
  if (!doc.size) return null;
  let best: { bid: ITenderBid; score: number } | null = null;
  for (const bid of tenders) {
    const words = tokens(`${bid.bid_title ?? ""} ${bid.client_name ?? ""}`);
    let score = 0;
    for (const w of words) {
      if (doc.has(w)) score += w.length >= 6 ? 2 : 1;
    }
    if (score >= 2 && (!best || score > best.score)) best = { bid, score };
  }
  return best?.bid ?? null;
};
